import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Checkbox,
    Group,
    Loader,
    Modal,
    ScrollArea,
    Stack,
    Text,
    UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconFolder, IconRefresh, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
    sharePointApi,
    type SharePointFolderViewModel,
    type SharePointSelection,
} from '@src/services/sharepointApi';
import './UploadSection.scss';

interface SharePointFolderPickerProps {
    opened: boolean;
    doctypeCode: string;
    disabled?: boolean;
    onClose: () => void;
    onFolderReady: (selection: SharePointSelection) => void;
}

const MAX_SELECTED_FILES = 200;

const formatBytes = (bytes: number | null): string => {
    if (!bytes) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
};

const SharePointFolderPicker: React.FC<SharePointFolderPickerProps> = ({
    opened,
    doctypeCode,
    disabled,
    onClose,
    onFolderReady,
}) => {
    const { t } = useTranslation();
    const [folder, setFolder] = useState<SharePointFolderViewModel>();
    const [currentPath, setCurrentPath] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

    const loadFolder = useCallback(async (path: string) => {
        setLoading(true);
        setError(undefined);
        try {
            const result = await sharePointApi.listItems(doctypeCode, path);
            setFolder(result);
            setCurrentPath(result.path);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : t('extraction.sharePoint.errors.load'));
        } finally {
            setLoading(false);
        }
    }, [doctypeCode, t]);

    useEffect(() => {
        if (!opened) return;
        setSelectedFiles([]);
        void loadFolder('');
    }, [opened, loadFolder]);

    const breadcrumbs = useMemo(() => {
        const parts = currentPath ? currentPath.split('/') : [];
        return [
            { label: folder?.root_folder || t('extraction.sharePoint.root'), path: '' },
            ...parts.map((part, index) => ({
                label: part,
                path: parts.slice(0, index + 1).join('/'),
            })),
        ];
    }, [currentPath, folder?.root_folder, t]);

    const selectFiles = (): void => {
        if (!selectedFiles.length) return;
        const name = selectedFiles.length === 1
            ? selectedFiles[0].split('/').pop() || 'PDF'
            : t('extraction.sharePoint.selectedFiles', { count: selectedFiles.length });
        onFolderReady({ path: currentPath, name, filePaths: selectedFiles });
        onClose();
    };

    const toggleFile = (path: string, checked: boolean): void => {
        setSelectedFiles((files) => {
            if (!checked) return files.filter((file) => file !== path);
            if (files.includes(path) || files.length >= MAX_SELECTED_FILES) return files;
            return [...files, path];
        });
    };

    const visiblePdfPaths = (folder?.items ?? [])
        .filter((item) => item.item_type === 'file')
        .map((item) => item.path);
    const selectedVisibleCount = visiblePdfPaths.filter((path) => selectedFiles.includes(path)).length;
    const allVisibleSelected = visiblePdfPaths.length > 0 && selectedVisibleCount === visiblePdfPaths.length;
    const canSelectAllVisible = selectedFiles.length + visiblePdfPaths.length - selectedVisibleCount <= MAX_SELECTED_FILES;

    const toggleAllVisible = (checked: boolean): void => {
        setSelectedFiles((files) => checked
            ? [...files, ...visiblePdfPaths.filter((path) => !files.includes(path))]
            : files.filter((path) => !visiblePdfPaths.includes(path))
        );
    };

    const removeFile = (path: string): void => {
        setSelectedFiles((files) => files.filter((file) => file !== path));
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('extraction.sharePoint.title')}
            size={960}
            centered
            classNames={{ content: 'sharepoint-picker-modal', header: 'sharepoint-picker-header', body: 'sharepoint-picker-body' }}
        >
            <Stack gap="md" className="sharepoint-picker-layout">
                <Text size="sm" c="dimmed">
                    {t('extraction.sharePoint.description')}
                </Text>

                {folder && (
                    <Stack gap="xs">
                        <Group justify="space-between" wrap="nowrap">
                            <Text size="sm" fw={600}>
                                {folder.site} · {folder.library}
                            </Text>
                            <Button
                                variant="subtle"
                                size="compact-xs"
                                leftSection={<IconRefresh size={14} />}
                                onClick={() => void loadFolder(currentPath)}
                                disabled={loading}
                            >
                                {t('common.refresh')}
                            </Button>
                        </Group>
                        <Group gap={4} wrap="wrap">
                            {breadcrumbs.map((crumb, index) => (
                                <React.Fragment key={crumb.path || 'root'}>
                                    {index > 0 && <Text size="xs" c="dimmed">/</Text>}
                                    <Button
                                        variant="subtle"
                                        size="compact-xs"
                                        onClick={() => void loadFolder(crumb.path)}
                                        disabled={loading}
                                    >
                                        {crumb.label}
                                    </Button>
                                </React.Fragment>
                            ))}
                        </Group>
                    </Stack>
                )}

                {error && (
                    <Alert color="red" icon={<IconAlertCircle size={16} />}>
                        {error}
                    </Alert>
                )}

                <Checkbox
                    label={t('extraction.sharePoint.selectAll')}
                    checked={allVisibleSelected}
                    indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                    onChange={(event) => toggleAllVisible(event.currentTarget.checked)}
                    disabled={disabled || loading || visiblePdfPaths.length === 0 || (!allVisibleSelected && !canSelectAllVisible)}
                />

                <ScrollArea className="sharepoint-picker-browser" type="auto" scrollbars="y" offsetScrollbars="present" scrollbarSize={8} overscrollBehavior="contain">
                    {loading ? (
                        <Group justify="center" py="xl">
                            <Loader size="sm" />
                        </Group>
                    ) : (
                        <Stack gap={4} pr="sm">
                            {(folder?.items ?? []).map((item) => item.item_type === 'folder' ? (
                                <Group
                                    key={item.path}
                                    justify="space-between"
                                    wrap="nowrap"
                                    className="sharepoint-item sharepoint-picker-item"
                                >
                                    <UnstyledButton
                                        className="sharepoint-folder-name"
                                        onClick={() => void loadFolder(item.path)}
                                        disabled={disabled || loading}
                                        aria-label={t('extraction.sharePoint.openFolder', { name: item.name })}
                                    >
                                        <Group gap="xs" wrap="nowrap" miw={0}>
                                            <IconFolder size={18} className="icon-primary" />
                                            <Text size="sm" truncate="end">{item.name}</Text>
                                        </Group>
                                    </UnstyledButton>
                                    <IconChevronRight size={14} aria-hidden="true" />
                                </Group>
                            ) : (
                                <Group
                                    key={item.path}
                                    justify="space-between"
                                    wrap="nowrap"
                                    className="sharepoint-item sharepoint-picker-item"
                                >
                                    <Group gap="xs" wrap="nowrap" miw={0}>
                                        <Checkbox
                                            checked={selectedFiles.includes(item.path)}
                                            onChange={(event) => toggleFile(item.path, event.currentTarget.checked)}
                                            aria-label={t('extraction.sharePoint.selectFile', { name: item.name })}
                                            disabled={disabled || (!selectedFiles.includes(item.path) && selectedFiles.length >= MAX_SELECTED_FILES)}
                                        />
                                        <Text size="sm" truncate="end">{item.name}</Text>
                                    </Group>
                                    <Badge variant="light" color="gray">
                                        {formatBytes(item.size) || 'PDF'}
                                    </Badge>
                                </Group>
                            ))}
                            {!folder?.items.length && (
                                <Text size="sm" c="dimmed" ta="center" py="xl">
                                    {t('extraction.sharePoint.empty')}
                                </Text>
                            )}
                        </Stack>
                    )}
                </ScrollArea>

                <Stack
                    gap="xs"
                    className={`sharepoint-selected-panel${selectedFiles.length ? ' has-files' : ''}`}
                    role="region"
                    aria-label={t('extraction.sharePoint.selectedList', { count: selectedFiles.length })}
                >
                    <Group justify="space-between">
                        <Text size="sm" fw={600}>
                            {t('extraction.sharePoint.selectedList', { count: selectedFiles.length })}
                        </Text>
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            onClick={() => setSelectedFiles([])}
                            disabled={disabled || selectedFiles.length === 0}
                        >
                            {t('extraction.sharePoint.clearAll')}
                        </Button>
                    </Group>
                    {selectedFiles.length === 0 ? (
                        <Text size="xs" c="dimmed">
                            {t('extraction.sharePoint.noFilesSelected')}
                        </Text>
                    ) : (
                        <ScrollArea className="sharepoint-picker-selected-scroll" type="auto" scrollbars="y" offsetScrollbars="present" scrollbarSize={8} overscrollBehavior="contain">
                            <Stack gap={4} pr="sm">
                                {selectedFiles.map((path) => (
                                    <Group key={path} justify="space-between" wrap="nowrap" gap="xs">
                                        <Text size="xs" style={{ overflowWrap: 'anywhere', minWidth: 0, flex: 1 }}>
                                            {path}
                                        </Text>
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            style={{ flexShrink: 0 }}
                                            aria-label={t('extraction.sharePoint.removeFile', { name: path })}
                                            onClick={() => removeFile(path)}
                                            disabled={disabled}
                                        >
                                            <IconX size={14} />
                                        </ActionIcon>
                                    </Group>
                                ))}
                            </Stack>
                        </ScrollArea>
                    )}
                    {selectedFiles.length >= MAX_SELECTED_FILES && (
                        <Text size="xs" c="orange">
                            {t('extraction.sharePoint.maxFiles', { count: MAX_SELECTED_FILES })}
                        </Text>
                    )}
                </Stack>

                <Group justify="flex-end" className="sharepoint-picker-actions">
                    <Button variant="default" onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={selectFiles}
                        disabled={disabled || loading || selectedFiles.length === 0}
                    >
                        {t('extraction.sharePoint.useFiles', { count: selectedFiles.length })}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default SharePointFolderPicker;
